import ProductService from '@/services/api/ProductService'

/**
 * Produto embutido no formulário do item (aula, plano ou espaço).
 *
 * O marketplace só vende produto: o item precisa de um produto com preço e
 * status "RE" para existir na vitrine. Este mixin esconde essa mecânica do
 * usuário — o formulário do item cria o produto por baixo (rascunho "CR"),
 * vincula o item (composição 1↔1, travada aqui no front) e publica no fim.
 *
 * Convenções:
 * - `product.*` é o estado do produto vinculado (ou a ser criado);
 * - o nome do produto segue o nome do item enquanto os dois estiverem iguais
 *   (produto legado com nome próprio não é sobrescrito);
 * - erros de gateway no save viram aviso, nunca perdem a edição (mesma regra
 *   da tela antiga de produto).
 */
export default {
  data: () => ({
    bindingService: null,
    /**
     * Leitura do anúncio em voo. Sem isto, entrar no passo de venda antes de
     * `loadLinkedProduct` responder fazia a tela concluir "não tem anúncio" e
     * criar um segundo — o antigo, com preço e fotos, virava órfão.
     */
    productLoadPromise: null,
    product: {
      id: null,
      name: '',
      price: null,
      status: 'CR',
      description: '',
      how_works: '',
      guidelines: '',
      legal_entity_id: null,
    },
    productFiles: [],
    productLoaded: false,
    productLoadFailed: false,
    /** Item vendido por mais de um produto (legado): editamos só o principal. */
    productMultiSale: false,
    productSubscriptionPlan: null,
    productRegisterOnGateway: false,
    productGatewayRegistered: false,
    productOriginalPrice: null,
    productPriceChangeConfirmed: false,
    /** Nome do produto seguia o do item quando carregado? Então continua seguindo. */
    productNameFollowsItem: true,
  }),

  computed: {
    productPublished() {
      return this.product.status === 'RE'
    },
    productExists() {
      return !!this.product.id
    },
  },

  methods: {
    productBindingService() {
      if (!this.bindingService) {
        this.bindingService = ProductService.build(this.$vs)
      }
      return this.bindingService
    },

    /**
     * Procura o produto que vende este item e adota o estado dele.
     * Item sem produto é situação normal (cadastro antigo ou inacabado).
     */
    loadLinkedProduct(itemType, itemId, itemName = '') {
      this.productLoadPromise = this.doLoadLinkedProduct(itemType, itemId, itemName)
      return this.productLoadPromise
    },

    async doLoadLinkedProduct(itemType, itemId, itemName = '') {
      const service = this.productBindingService()
      this.productLoaded = false
      this.productLoadFailed = false
      try {
        const rows = await service.getItemsMap(itemType, itemId)
        const list = Array.isArray(rows) ? rows : []
        this.productMultiSale = list.length > 1
        if (!list.length) {
          this.productLoaded = true
          return null
        }
        const full = await service.read(this.pickBestSale(list).product_id)
        this.adoptProduct(full, itemName)
        this.productLoaded = true
        return full
      } catch (error) {
        // Sem permissão de produto (ou API fora): a tela do item continua útil,
        // só a parte de venda fica indisponível — e, principalmente, não criamos
        // um anúncio novo por cima de um que pode existir e não conseguimos ler.
        this.productLoaded = true
        this.productLoadFailed = true
        return null
      }
    },

    /**
     * Qual anúncio representa o item quando há mais de um (dado legado).
     * Mesma ordem do servidor: publicado > com preço > mais antigo. Pegar o
     * primeiro da lista trazia o rascunho vazio na frente do que está vendendo.
     */
    pickBestSale(list) {
      return list.slice().sort((a, b) => {
        const publicado = (row) => (row.status === 'RE' ? 0 : 1)
        const comPreco = (row) => (Number(row.price) > 0 ? 0 : 1)
        return publicado(a) - publicado(b)
          || comPreco(a) - comPreco(b)
          || Number(a.product_id) - Number(b.product_id)
      })[0]
    },

    adoptProduct(full, itemName = '') {
      this.product = {
        id: full.id,
        name: full.name || '',
        price: full.price === null || full.price === undefined ? null : Number(full.price),
        status: full.status || 'CR',
        description: full.description || '',
        how_works: full.how_works || '',
        guidelines: full.guidelines || '',
        legal_entity_id: full.legal_entity_id || null,
      }
      this.productFiles = Array.isArray(full.files) ? full.files : []
      this.productSubscriptionPlan = full.subscription_plan || null
      this.productRegisterOnGateway = !!(full.subscription_plan && full.subscription_plan.register_on_payment_gateway)
      this.productGatewayRegistered = !!(full.subscription_plan && full.subscription_plan.gateway_registered)
      this.productOriginalPrice = this.product.price
      this.productPriceChangeConfirmed = false
      this.productNameFollowsItem = !itemName || full.name === itemName
    },

    /**
     * Garante que o produto existe e está vinculado ao item. Chamado ao entrar
     * no passo "Preço e fotos": upload de foto e publicação precisam do id.
     * O produto nasce rascunho ("CR") — invisível até o "Publicar" do fim.
     */
    async ensureProduct({ itemType, itemId, itemName, legalEntityId }) {
      // Uma leitura em voo decide isto: sem esperar, um clique rápido no passo
      // de venda concluía "não tem anúncio" e criava um segundo por cima.
      if (this.productLoadPromise) {
        await this.productLoadPromise.catch(() => null)
      }
      if (this.product.id) {
        return this.product
      }
      // Leitura falhou: existir ou não é desconhecido, e criar às cegas é o que
      // duplicava o anúncio. Melhor a tela dizer que a venda está indisponível.
      if (this.productLoadFailed) {
        throw new Error('sale-unavailable')
      }

      // Uma chamada só, decidida no servidor e idempotente (POST /products/items/ensure):
      // devolve o anúncio existente ou cria o rascunho já vinculado ao item.
      const full = await this.productBindingService().ensureItemProduct(itemType, itemId, itemName)
      this.adoptProduct(full, itemName)
      if (!this.product.legal_entity_id) {
        this.product.legal_entity_id = legalEntityId
      }
      return full
    },

    /**
     * Grava o produto com o status pedido. `itemName`/`legalEntityId` mantêm
     * nome e responsável em sincronia com o item quando aplicável.
     */
    async saveProductAs(status, { itemName = null, legalEntityId = null } = {}) {
      const service = this.productBindingService()
      const name = this.productNameFollowsItem && itemName ? itemName : this.product.name

      const payload = {
        id: this.product.id,
        name,
        price: this.product.price === null ? 0 : this.product.price,
        status,
        description: this.product.description || null,
        how_works: this.product.how_works || null,
        guidelines: this.product.guidelines || null,
        legal_entity_id: legalEntityId || this.product.legal_entity_id,
        confirm_price_change: this.productPriceChangeConfirmed,
      }

      const data = await service.createOrUpdate(payload)

      this.product.name = data.name || name
      this.product.status = data.status || status
      this.productOriginalPrice = data.price === null || data.price === undefined ? null : Number(data.price)
      this.productPriceChangeConfirmed = false

      // O save deu certo mas o registro no gateway pode ter falhado; sumir com
      // esse aviso deixava o clube achando que tinha um plano vendável.
      if (data.gateway_registration_warning) {
        this.notifyWarning(this.$vs, data.gateway_registration_warning, 10000)
      }

      return data
    },

    /**
     * Repreçar assinatura muda o valor de quem já assinou (PagBank aplica na
     * próxima fatura). O backend recusa sem confirmação; aqui perguntamos antes.
     */
    productNeedsPriceConfirmation() {
      return !!this.productSubscriptionPlan
        && !this.productPriceChangeConfirmed
        && this.productOriginalPrice !== null
        && Number(this.product.price) !== Number(this.productOriginalPrice)
    },

    confirmProductPriceChange(onConfirm) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'warning',
        title: this.$t('product.price_change_title'),
        acceptText: this.$t('product.price_change_accept'),
        cancelText: 'Cancelar',
        text: this.$t('product.price_change_warning'),
        accept: () => {
          this.productPriceChangeConfirmed = true
          onConfirm()
        },
      })
    },

    onProductFileUploaded(media) {
      this.productFiles.push(media)
    },

    async deleteProductFile(media) {
      const service = this.productBindingService()
      try {
        const remaining = await service.deleteFile(this.product.id, media.id)
        this.productFiles = Array.isArray(remaining) ? remaining : this.productFiles.filter(item => item.id !== media.id)
      } catch (error) {
        this.notifyErrors(this.$vs, error)
      }
    },
  },
}
